import { CrudModes } from "@app/shared/components/abstracts/base-component";

export class JsonSettingsParms {
  constructor(
    public componentId: string,
    public menuId: string,
    public previousViewState: any,
    public roleId?: string,
    public language?: string,
    public mode?: CrudModes
  ) {}
}

export class JsonSettingsTranslationParms {
  constructor(
    public componentId: string,
    public previousViewState: any,
    public cancelViewState: any,
    public formViewState: any
  ) {}
}

export interface JsonSettingsConfig {
  label: string;
  type: JsonSettingTypes;
  path: string;
  clientCodes?: string[];
  translationPath?: string;
  sharedComponents?: string[];
  sharedComponentTranslationPaths?: string[];
  description?: string;
  internalValues?: JsonSettingsConfigInternalValues[];
  defaultValue?: any;
  valueSettings?: JsonSettingsConfig[];
  indent?: boolean;
  sequence?: number;
  componentId?: string;
  categorySectionEnabled?: boolean;
  hidden?: boolean;
}

export interface JsonSettingsConfigInternalValues {
  value: string;
  description: string;
}

export interface JsonOptionsArray {
  enabled: boolean;
  value: any;
  label: string;
}

export enum JsonSettingTypes {
  section = "S",
  category = "C",
  category_section = "CS",
  text = "T",
  numeric = "N",
  boolean = "B",
  list = "L"
}

export interface CategoryPath {
  path: string;
  label: string;
  description: string;
  sequence: number;
}
export interface JsonInternalList {
  value: string | number;
  description: string;
}

export interface JsonFilter {
  list: JsonSettingsBase[];
  filter: any;
}

export interface JsonSave {
  data: any;
  route: boolean;
  componentId: string;
  pathsUpdated: JsonPathsUpdated[];
}

export interface JsonPathsUpdated {
  path: string;
  value: string | boolean;
  defaultValue: string | boolean;
}

export class JsonSettingsFormControl {
  private _controlName: string;

  constructor(name: string) {
    this._controlName = name;
  }

  public get controlName(): string {
    return this._controlName;
  }

  public set controlName(name: string) {
    this._controlName = name;
  }
}

/**
 * Handles standard fields with no additional functionality
 */
export abstract class JsonSettingsBase {
  private _type: string;
  private _title: string;
  private _description: string;
  private _path: string;
  private _sectionIndex?: number;
  private _formControlNames: JsonSettingsFormControl[] = [];
  private _internalList: JsonInternalList[] = [];
  private _valueSettings: JsonSettingsBase[] = [];
  private _categoryIndex: number;
  // This is used to change behavior of the json-settings component
  private _isTranslations: boolean;
  private _defaultValue: any;
  private _indent: boolean;
  private _sequence: number;
  private _hidden: boolean;
  private _componentId?: string;
  private _categorySectionEnabled?: boolean;

  constructor(
    type: string,
    title: string,
    description: string,
    path?: string,
    sectionIndex?: number,
    internalList?: JsonInternalList[],
    valueSettings?: JsonSettingsBase[],
    isTranslations?: boolean,
    categoryIndex?: number,
    defaultValue?: any,
    indent?: boolean,
    sequence?: number | string,
    hidden?: boolean,
    componentId?: string,
    categorySectionEnabled?: boolean
  ) {
    this._type = type;
    this._title = title;
    this._description = description;
    this._path = path;
    this._sectionIndex = sectionIndex;
    this._internalList = internalList;
    this._valueSettings = valueSettings ?? [];
    this._isTranslations = isTranslations;
    this._categoryIndex = categoryIndex;
    this._defaultValue = defaultValue;
    this._indent = indent;
    this._sequence = typeof sequence === "string" ? parseFloat(sequence) : sequence;
    this._hidden = hidden;
    this._componentId = componentId;
    this._categorySectionEnabled = categorySectionEnabled;
  }

  get type(): string {
    return this._type;
  }

  get title(): string {
    return this._title;
  }

  get description(): string {
    return this._description;
  }

  get formControlNames(): JsonSettingsFormControl[] {
    return this._formControlNames;
  }

  get sectionIndex(): number {
    return this._sectionIndex;
  }

  get path(): string {
    return this._path;
  }

  get internalList(): JsonInternalList[] {
    return this._internalList;
  }

  get valueSettings(): JsonSettingsBase[] {
    return this._valueSettings;
  }

  set valueSettings(vals: JsonSettingsBase[]) {
    this._valueSettings = vals;
  }

  get isTranslations(): boolean {
    return this._isTranslations;
  }

  get categoryIndex(): number {
    return this._categoryIndex;
  }

  get defaultValue(): any {
    return this._defaultValue;
  }

  /**
   * A flag to determine if the title should be indented even more. This is typically used when there are multiple cateogries inside a section.
   */
  get indent(): boolean {
    return this._indent;
  }

  get sequence(): number {
    return this._sequence;
  }

  get hidden(): boolean {
    return this._hidden;
  }

  get componentId(): string {
    return this._componentId;
  }

  get categorySectionEnabled(): boolean {
    return this._categorySectionEnabled;
  }
}

/**
 * Section
 */
export class SectionSetting extends JsonSettingsBase {
  constructor(title: string, path: string, componentId?: string) {
    super(JsonSettingTypes.section, title, "", path, null, null, null, null, null, null, null, null, null, componentId);
  }
}

/**
 * Category
 */
export class CategorySetting extends JsonSettingsBase {
  constructor(title: string, description: string, sectionIndex: number, path: string, categoryIndex: number, sequence: number, hidden?: boolean) {
    super(JsonSettingTypes.category, title, description, path, sectionIndex, [], [], false, categoryIndex, undefined, false, sequence, hidden);
  }
}

/**
 * Category Section
 */
export class CategorySectionSetting extends JsonSettingsBase {
  constructor(title: string, description: string, sectionIndex: number, path: string, categoryIndex: number, hidden: boolean) {
    super(
      JsonSettingTypes.category_section,
      title,
      description,
      path,
      sectionIndex,
      [],
      [],
      false,
      categoryIndex,
      undefined,
      undefined,
      undefined,
      hidden
    );
  }
}

/**
 * Boolean field
 */
export class BooleanSetting extends JsonSettingsBase {
  constructor(
    title: string,
    description: string,
    path: string,
    sectionIndex: number,
    isTranslations: boolean,
    categoryIndex: number,
    defaultValue: any,
    indent?: boolean,
    categorySectionEnabled?: boolean,
    hidden?: boolean
  ) {
    super(
      JsonSettingTypes.boolean,
      title,
      description,
      path,
      sectionIndex,
      [],
      [],
      isTranslations,
      categoryIndex,
      defaultValue,
      indent,
      null,
      hidden,
      null,
      categorySectionEnabled
    );
  }
}

/**
 * Input field for text
 */
export class TextSetting extends JsonSettingsBase {
  constructor(
    title: string,
    description: string,
    path: string,
    sectionIndex: number,
    isTranslations: boolean,
    categoryIndex: number,
    defaultValue: any,
    internalList?: JsonInternalList[],
    indent?: boolean,
    sequence?: number
  ) {
    super(
      JsonSettingTypes.text,
      title,
      description,
      path,
      sectionIndex,
      internalList,
      [],
      isTranslations,
      categoryIndex,
      defaultValue,
      indent,
      sequence
    );
  }
}

/**
 * Input field for numerics
 */
export class NumberSetting extends JsonSettingsBase {
  constructor(
    title: string,
    description: string,
    path: string,
    sectionIndex: number,
    isTranslations: boolean,
    categoryIndex: number,
    defaultValue: any,
    internalList?: JsonInternalList[],
    indent?: boolean
  ) {
    super(JsonSettingTypes.numeric, title, description, path, sectionIndex, internalList, [], isTranslations, categoryIndex, defaultValue, indent);
  }
}

/**
 * Input field for arrays/lists
 */
export class ListSetting extends JsonSettingsBase {
  constructor(title: string, description: string, path: string, sectionIndex: number, isTranslations: boolean, categoryIndex: number) {
    super(JsonSettingTypes.list, title, description, path, sectionIndex, [], [], isTranslations, categoryIndex);
  }
}
